import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import FormInputField from '../FormInputField';
import Button, { buttonType } from '../../components/Button';
import Loader from '../Loader';
import { useLabels } from '../../hooks';
import { UserContext } from '../../contexts';
import { userApi } from '../../api';
import { formatString, getTextWithoutHtml, scrollTop } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import styles from './SignupForm.module.scss';
import loginStyles from '../LoginForm/LoginForm.module.scss';
import { SmsIcon } from '../LoginForm/CodeOptionIcons';
import { trackEvent } from '../../utils/analytics';

const SignupConfirm = () => {
    const [ showLoader, setShowLoader ] = useState(false);
    const [ error, setError ] = useState();
    const [ smsMaskedPhone, setSmsMaskedPhone ] = useState(null);
    const [ smsSent, setSmsSent ] = useState(false);
    const [ smsSending, setSmsSending ] = useState(false);
    const [ smsError, setSmsError ] = useState(null);
    const { getLabel, getCognitoApiError, getLocalUrlByLanguage } = useLabels();
    const { codeDeliveryDetails, confirmAccount, resendConfirmationAccountCode, signIn } = useContext(UserContext);
    const isActive = useRef(false);
    const confirmAccountCodeRef = useRef();
    const navigate = useNavigate();
    const username = codeDeliveryDetails?.username;

    useEffect(() => {
        isActive.current = true;

        // Reset scroll position when component mounts using the utility function
        scrollTop(0, 'auto');

        return () => {
            isActive.current = false;
        };
    }, []);

    const getConfirmText = useCallback(() => {
        if (codeDeliveryDetails) {
            return formatString(
                getLabel(
                    codeDeliveryDetails.DeliveryMedium === 'EMAIL' ? 
                    'signup_form_confirm_account_by_email' : 
                    'signup_form_confirm_account_by_phone'
                ), 
                `<span class="${styles.ltr_data}">${getTextWithoutHtml(codeDeliveryDetails.Destination)}</span>`
            );
        }
        return '';
    }, [codeDeliveryDetails, getLabel]);

    useEffect(() => {
        if (!username) return;
        userApi.GetSmsOption(username, true)
            .then(res => { if (isActive.current && res?.hasPhone) setSmsMaskedPhone(res.maskedPhone); })
            .catch(() => {});
    }, [username]);

    const handleSendViaSms = useCallback(async () => {
        setSmsSending(true);
        setSmsError(null);
        try {
            const res = await userApi.SendSmsOtp(username, true);
            if (!isActive.current) return;
            if (res?.success) {
                trackEvent('otp_requested', { method: 'sms' });
                setSmsSent(true);
            } else {
                setSmsError(getLabel('sms_no_phone'));
            }
        } catch {
            if (isActive.current) setSmsError(getLabel('sms_no_phone'));
        } finally {
            if (isActive.current) setSmsSending(false);
        }
    }, [username, getLabel]);

    const onConfirmAccountButtonClickHandle = useCallback((event) => {

        event?.preventDefault();
        let focusField = null;

        if (!confirmAccountCodeRef.current.validation()) {
            focusField = focusField || confirmAccountCodeRef.current;
        }

        if (focusField) {
            focusField.focus();
            return;
        }

        setError(null);
        setShowLoader(true);

        const code = confirmAccountCodeRef.current.getValue();
        const otpSent = smsSent;

        // OTP sent by us -> confirm server-side; otherwise use the Cognito email code.
        const confirm = otpSent
            ? userApi.VerifySmsOtpAndConfirmAccount(username, code).then(res => {
                if (!res?.success) throw new Error(getLabel('otp_expired_or_invalid'));
            })
            : confirmAccount(code);

        confirm.then(() => {
            if (isActive.current) {
                signIn(codeDeliveryDetails.username, codeDeliveryDetails.password)
                    .then(() => {
                        navigate(getLocalUrlByLanguage('/purchase'));
                    })
                    .catch(error => {
                        if (isActive.current) {
                            setError(error);
                            setShowLoader(false);
                        }
                    });
            }
        }).catch(error => {
            if (isActive.current) {
                setError(error);
                setShowLoader(false);
            }
        });
    }, [codeDeliveryDetails, confirmAccount, signIn, navigate, getLocalUrlByLanguage, smsSent, username, getLabel]);

    const onResendConfirmationAccountCodeButtonClickHandle = useCallback((event) => {
        event?.preventDefault();

        setSmsSent(false);
        setSmsError(null);
        setShowLoader(true);

        resendConfirmationAccountCode(codeDeliveryDetails.username, codeDeliveryDetails.password).then(() => {
            if (isActive.current) {
                setShowLoader(false);
            }
        }).catch(error => {
            if (isActive.current) {
                setError(error);
                setShowLoader(false);
            }
        });
    }, [codeDeliveryDetails, resendConfirmationAccountCode]);

    return <>
        <div className={`${styles.signup} ${styles.signup_confirm}`}>
            <div>
                <form className={styles.form} onSubmit={onConfirmAccountButtonClickHandle}>
                    <div className={styles.confirm_text} dangerouslySetInnerHTML={{ __html: getConfirmText()}} />
                    {smsSent && <div className={styles.confirm_text}>{formatString(getLabel('sms_sent_to_phone'), smsMaskedPhone)}</div>}
                    <FormInputField ref={confirmAccountCodeRef} label={getLabel('signup_form_confirm_account_code_field')} placeholder={getLabel('signup_form_confirm_account_code_field')} required={true} autoComplete="one-time-code" inputMode="numeric" heContentDirection={'ltr'} hePlaceholderDirection={'rtl'} />
                    <button hidden={true}></button>
                </form>
                <div className={`${styles.resend_code} ${smsMaskedPhone ? styles.has_code_options : ''}`}>
                    <Button type={buttonType.link} className={styles.resend_code_button} onClick={onResendConfirmationAccountCodeButtonClickHandle} >{getLabel('signup_form_confirm_account_resend_code_button')}</Button>
                </div>
                <div className={loginStyles.codeOptions}>
                    {smsMaskedPhone && !smsSent && (
                        <button type="button" className={loginStyles.codeOption} disabled={smsSending} onClick={handleSendViaSms}>
                            <SmsIcon />
                            <span>{formatString(getLabel('sms_option_label'), smsMaskedPhone)}</span>
                        </button>
                    )}
                    {smsError && <div className={styles.error}>{smsError}</div>}
                </div>
            </div>
            <div className={styles.buttons}>
                <div className={styles.error} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error)}} />
                <Button type={buttonType.premium} className={styles.button} onClick={onConfirmAccountButtonClickHandle} >{getLabel('signup_form_confirm_account_confirm_button')}</Button>
            </div>
        </div>
        {showLoader && <Loader />}
    </>;
};

export default SignupConfirm;